import {addDays, parseISO, startOfToday} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {showNotification} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, isNil, isNotNil} from 'ramda';

import {
  InvoicingDocumentItemResponseBody,
  InvoicingDocumentType,
  useGetTenantQuery,
} from '@dms/api/core';
import {
  AccountingDocumentRecalculateApiRes,
  PaymentInfoRequestBody,
  useInvoicingDocumentRecalculateMutation,
} from '@dms/api/finance';
import {
  CorrectiveTaxDocumentItemRequestBody,
  CorrectiveTaxDocumentRequestBody,
  useGetBusinessCaseQuery,
  useGetBusinessCaseSeriesDefinitionListQuery,
  useGetOrderQuery,
} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  getBankAccountDetails,
  getBusinessCaseCorrectiveTaxDocumentSeriesId,
  getDefaultBankAccountFromBranch,
  handleApiError,
  useGetBalanceInvoiceCorrectiveItems,
  useGetCurrentBranch,
  useGetDocumentData,
  useSubmitCorrectiveTaxDocument,
} from '@dms/shared';

import {getApiDateString, Nullish} from 'shared/web';

// `dms-business-case` and `dms-accounting` are both tagged `type:module`; the Nx boundary rule
// forbids a `type:module` from depending on another `type:module`, regardless of how "pure" the
// individual accounting-internal file is. That rules out importing `PERCENTAGE_SIGN` from
// `libs/dms/modules/accounting/src/constants.ts`, `mapInvoiceItems`/`getInvoicingDocumentType`
// from their respective `CreateCorrectiveTaxDocument` util files, and
// `mapInvoiceItemsForRecalculation` from `useGetInvoiceItemsForRecalculation` — every one of
// those files is reimplemented inline below instead. (The bank-account helpers and
// `useGetBalanceInvoiceCorrectiveItems` were relocated to `@dms/shared` instead, since they had
// zero dms-accounting-internal type dependency and are needed byte-for-byte here.)
const PERCENTAGE_SIGN = '%';

// Mirrors the manual-form default in the (accounting-internal, not importable)
// `getDefaultCorrectiveTaxDocumentFormValues.ts`.
const CORRECTIVE_TAX_DUE_DAYS = 14;

export type UseCreateCheckoutCorrectiveTaxDocumentDirectlyArgs = {
  businessCaseId: string;
  orderId: string;
  checkoutId: string;
  paymentId: string;
  invoicingDocumentId: string | Nullish;
  invoiceType: string;
  isSellerAsSupplier: boolean;
  enabled: boolean;
};

// Reimplementation of `getInvoicingDocumentType` (accounting-internal, see boundary note above).
const getInvoicingDocumentType = (invoiceType: string): InvoicingDocumentType =>
  match(invoiceType)
    .with('BALANCE_INVOICE', always('balance_invoice' as const))
    .with('PROFORMA', always('invoice_proforma' as const))
    .with('TAX_DOCUMENT_FOR_PAYMENT', always('tax_document_for_payment' as const))
    .otherwise(always('invoice' as const));

const negateAmount = (amount: string) => (-parseFloat(amount)).toString();

// Reimplementation of `mapInvoiceItems` (accounting-internal). `shouldNegate` is `true` for the
// general item list (a corrective/credit document reverses the original amounts) and `false`
// for the already-settings-filtered `BALANCE_INVOICE` item list below - matching the deleted
// hook's own `mapInvoiceItems(items, shouldNegate)` calls exactly. 'full' vs. 'partial' only
// selects which submission endpoint is called (see `useSubmitCorrectiveTaxDocument`), it does
// not affect negation.
const mapInvoiceItems = (
  items: InvoicingDocumentItemResponseBody[] | Nullish,
  shouldNegate: boolean
) =>
  (items ?? []).map((item) => ({
    description: item.description,
    isUnitPriceWithVat: item.isUnitPriceWithVat,
    itemId: item.itemId,
    type: item.type,
    vatType: item.vatType,
    quantity:
      item.type === 'discount' && item.unit === PERCENTAGE_SIGN
        ? item.discount?.percent
        : item.quantity,
    pricePerUnit: shouldNegate ? negateAmount(item.pricePerUnit.amount) : item.pricePerUnit.amount,
    relatedItemId: item.relatedItemId,
    unit: item.unit,
  }));

type MappedInvoiceItem = ReturnType<typeof mapInvoiceItems>[number];

// Reimplementation of `mapInvoiceItemsForRecalculation(items, currency, isCorrectiveTaxDocument: true)`
// (accounting-internal). With `isCorrectiveTaxDocument` always true here, the source's extra
// discount-unit-price negation branch never applies, so it is omitted.
const buildRecalculationItems = (items: MappedInvoiceItem[], currency: string) =>
  items
    .map((item) => ({
      unitType: item.unit === PERCENTAGE_SIGN ? ('percent' as const) : ('standard' as const),
      currency,
      unitPrice: item.pricePerUnit,
      unit: item.unit,
      description: item.description,
      isUnitPriceWithVat: item.isUnitPriceWithVat,
      itemId: item.itemId,
      relatedItemId: item.relatedItemId,
      type: item.type,
      vatType: item.vatType,
      quantity: item.quantity?.toString() || '0',
    }))
    .filter(
      (item) =>
        !(
          (item.type === 'discount' &&
            ((item.unitPrice === '0' && item.unit !== PERCENTAGE_SIGN) ||
              (item.quantity === '0' && item.unit === PERCENTAGE_SIGN))) ||
          (item.type === 'standard' && item.quantity === '0' && item.unitPrice === '0')
        )
    );

// Reimplementation of the item-mapping half of `useGetCorrectiveTaxForBCDocumentBody`'s
// `getRequestBody` (accounting-internal).
const buildCorrectiveTaxDocumentItems = (
  items: MappedInvoiceItem[],
  currency: string,
  summary: AccountingDocumentRecalculateApiRes | Nullish
): CorrectiveTaxDocumentItemRequestBody[] =>
  items.map((item) => {
    const discount =
      summary?.items.find(
        (summaryItem) => summaryItem.itemId === item.itemId && summaryItem.type === 'discount'
      )?.discount ?? null;

    return {
      currency,
      description: item.description,
      isUnitPriceWithVat: item.isUnitPriceWithVat,
      itemId: item.itemId,
      pricePerUnit: item.pricePerUnit ?? '0',
      quantity: item.unit === PERCENTAGE_SIGN ? '1' : (item.quantity?.toString() ?? '0'),
      type: item.type,
      unit: item.unit,
      correctedItemId: item.itemId,
      vatType: item.vatType,
      discount: item.type === 'discount' ? discount : null,
      relatedItemId: item.relatedItemId,
    };
  });

// `accountingDocument.paymentInfo.paymentMethod` (from `useGetDocumentData`) can be
// `'FREE_OF_CHARGE'`, a value `PaymentInfoRequestBody` has no slot for (it only allows
// BANK_TRANSFER/CARD/CASH/OFFSET). Decision: fall back to `BANK_TRANSFER` — the same default
// `useGetDocumentData` itself already applies when the source payment method is nullish, so this
// keeps a free-of-charge original document consistent with the rest of this flow rather than
// introducing a new assumption.
const narrowPaymentMethod = (
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET' | 'FREE_OF_CHARGE'
): PaymentInfoRequestBody['paymentMethod'] =>
  match(paymentMethod)
    .with('BANK_TRANSFER', () => 'BANK_TRANSFER' as const)
    .with('CARD', () => 'CARD' as const)
    .with('CASH', () => 'CASH' as const)
    .with('OFFSET', () => 'OFFSET' as const)
    .with('FREE_OF_CHARGE', () => 'BANK_TRANSFER' as const)
    .exhaustive();

// `PaymentTypeSchema` (the recalculation endpoint's payment-type enum) has no "offset" value.
// Decision: treat an offset settlement as a bank transfer for this VAT/cash-rounding calculation
// — the calculation only distinguishes cash from non-cash payment channels, and offset is a
// non-cash settlement.
const toRecalculationPaymentType = (paymentMethod: PaymentInfoRequestBody['paymentMethod']) =>
  match(paymentMethod)
    .with('CARD', () => 'payment_card' as const)
    .with('CASH', () => 'cash' as const)
    .with('BANK_TRANSFER', () => 'bank_transfer' as const)
    .with('OFFSET', () => 'bank_transfer' as const)
    .exhaustive();

// An "amount" other than 1 / 100 / 1000 has no valid representation in this field's literal
// union. Decision: treat it the same as "no exchange rate ratio" rather than send a malformed
// value.
const buildExchangeRateRatioAmount = (amount: string) =>
  match(Number(amount))
    .with(1, () => 1 as const)
    .with(100, () => 100 as const)
    .with(1000, () => 1000 as const)
    .otherwise(() => null);

export const useCreateCheckoutCorrectiveTaxDocumentDirectly = ({
  businessCaseId,
  orderId,
  checkoutId,
  paymentId,
  invoicingDocumentId,
  invoiceType,
  isSellerAsSupplier,
  enabled,
}: UseCreateCheckoutCorrectiveTaxDocumentDirectlyArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const formatDateTime = useDateTimeFormatter();

  // `useGetTenantQuery` takes no query arg and is already called unconditionally elsewhere in the
  // app; RTK Query dedupes identical unparametrized queries, so no skip/skipToken is needed here.
  const {data: tenant} = useGetTenantQuery();
  const {
    activeBranchId,
    data: currentBranch,
    isSynced: isBranchSynced,
  } = useGetCurrentBranch({refetchOnFocus: true});

  const {data: accountingDocument} = useGetDocumentData(
    enabled ? invoiceType : null,
    enabled ? invoicingDocumentId : null
  );

  // Mirrors the manual form's `isBalanceInvoiceFromCheckout`: `BALANCE_INVOICE` corrections read
  // their items from the branch-setting-aware `useGetBalanceInvoiceCorrectiveItems`. Unlike the
  // manual form, this flow never navigates, so it has no `type=partial` URL param to read at
  // render time — it reads the raw item list plus the setting-only exclusion flag here, and
  // combines that with the actual click-time `type` argument inside `createCorrectiveTaxDocument`
  // below (see the `shouldExcludeProformaDeduction` there).
  const isBalanceInvoiceFromCheckout = enabled && invoiceType === 'BALANCE_INVOICE';
  const {
    rawBalanceInvoiceItems,
    isLoadingBalanceItems,
    isBalanceItemsError,
    balanceItemsError,
    shouldExcludeProformaDeductionBySetting,
  } = useGetBalanceInvoiceCorrectiveItems(
    isBalanceInvoiceFromCheckout,
    invoicingDocumentId ?? undefined
  );

  // `orderId`/`checkoutId`/`businessCaseId` are required (non-nullish) args on this hook, unlike
  // the accounting-internal reference hook, which also accepts `Nullish` values — so `enabled` is
  // the only skip condition needed here.
  const {data: documentSeries} = useGetBusinessCaseSeriesDefinitionListQuery(undefined, {
    skip: !enabled,
  });
  const {data: order} = useGetOrderQuery({orderId, checkoutId}, {skip: !enabled});
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId}, {skip: !enabled});

  const [calculateTaxDocument] = useInvoicingDocumentRecalculateMutation();
  const [submitCorrectiveTaxDocumentToBackend] = useSubmitCorrectiveTaxDocument();

  const isBrokerageBusinessCase = isNotNil(businessCase?.brokerageBusinessCase);
  const activeDefinition =
    documentSeries?.find((item) => item.branchId === activeBranchId) ??
    documentSeries?.find((item) => item.systemDefault) ??
    null;

  const documentSeriesId = getBusinessCaseCorrectiveTaxDocumentSeriesId({
    orderDiscriminator: order?.orderDiscriminator,
    isBrokerageBusinessCase,
    isSellerAsSupplier,
    seriesDefinition: activeDefinition,
  });

  const createCorrectiveTaxDocument = async (type: 'full' | 'partial') => {
    // `isLoadingBalanceItems`/`isBalanceItemsError` only carry meaning for the BALANCE_INVOICE
    // branch (they reflect `useGetBalanceInvoiceCorrectiveItems`'s own queries, both skipped
    // outside that branch), so they only gate readiness when `isBalanceInvoiceFromCheckout`.
    const isBalanceItemsNotReady =
      isBalanceInvoiceFromCheckout && (isLoadingBalanceItems || isBalanceItemsError);

    if (
      isNil(invoicingDocumentId) ||
      isNil(tenant) ||
      isNil(activeBranchId) ||
      isNil(accountingDocument) ||
      isNil(order) ||
      isNil(businessCase) ||
      isNil(documentSeries) ||
      !isBranchSynced ||
      isBalanceItemsNotReady
    ) {
      // The loading case is a normal, brief race (the dropdown trigger is disabled meanwhile —
      // see `useGetPaymentDetailAttributes.tsx`'s `isReady` usage); the error case is a genuine
      // failure the user should be told about rather than a silent no-op.
      if (isBalanceInvoiceFromCheckout && isBalanceItemsError && isNotNil(balanceItemsError)) {
        handleApiError(balanceItemsError);
      }
      return;
    }

    const resolvedInvoicingDocumentId = invoicingDocumentId;
    const resolvedTenant = tenant;
    const resolvedBranchId = activeBranchId;
    const resolvedAccountingDocument = accountingDocument;

    setIsLoading(true);

    // The setting-driven exclusion (`shouldExcludeProformaDeductionBySetting`, read at render
    // time) is combined here with the actual user-selected `type` (only known at click time) —
    // matching the manual form's own `createCorrectiveTaxDocumentType === 'without_proforma' ||
    // type === 'partial'` decision exactly, instead of a URL param that doesn't exist on this
    // direct-checkout flow (it never navigates).
    const shouldExcludeProformaDeduction =
      shouldExcludeProformaDeductionBySetting || type === 'partial';

    // Matches the deleted hook's own `mapInvoiceItems(items, shouldNegate)` calls: balance-invoice
    // items are filtered here (per the decision above) and left un-negated; the general item list
    // is filtered here (excluding `proforma_deduction`) and negated, since a corrective/credit
    // document reverses the original amounts.
    const mappedItems = isBalanceInvoiceFromCheckout
      ? mapInvoiceItems(
          rawBalanceInvoiceItems?.filter(
            (item) =>
              !shouldExcludeProformaDeduction || item.generatedItemType !== 'proforma_deduction'
          ),
          false
        )
      : mapInvoiceItems(
          resolvedAccountingDocument.items?.filter(
            (item) => item.generatedItemType !== 'proforma_deduction'
          ),
          true
        );
    const recalculationItems = buildRecalculationItems(mappedItems, resolvedTenant.currency);
    const paymentMethod = narrowPaymentMethod(resolvedAccountingDocument.paymentInfo.paymentMethod);

    // Reproduces `getDefaultCorrectiveTaxDocumentFormValues.ts`'s `defaultPaymentInfo` branching:
    // `isSellerAsSupplier` (mode 2) carries the
    // original document's own bank account over as-is; otherwise the bank account is re-derived
    // from the active branch's billing information (falling back to its primary/first account),
    // exactly like the manual "create corrective tax document" form does.
    const originBankAccount = resolvedAccountingDocument.paymentInfo.bankAccount;
    const defaultBankAccountResult = getDefaultBankAccountFromBranch(
      originBankAccount,
      currentBranch,
      isBranchSynced,
      false
    );
    const bankAccountDetails = getBankAccountDetails(
      defaultBankAccountResult.selectedBankAccount,
      defaultBankAccountResult.availableBankAccounts
    );
    const defaultPaymentInfo = isSellerAsSupplier
      ? {
          bankAccount: originBankAccount,
          iban: resolvedAccountingDocument.paymentInfo.iban,
          swift: resolvedAccountingDocument.paymentInfo.swift,
        }
      : {
          bankAccount: defaultBankAccountResult.selectedBankAccount,
          iban: bankAccountDetails.iban,
          swift: bankAccountDetails.swift,
        };

    const paymentInfo: PaymentInfoRequestBody = {
      paymentMethod,
      bankName: resolvedAccountingDocument.paymentInfo.bankName,
      bankAccount: defaultPaymentInfo.bankAccount,
      iban: defaultPaymentInfo.iban,
      swift: defaultPaymentInfo.swift,
      variableSymbol: resolvedAccountingDocument.paymentInfo.variableSymbol,
      constantSymbol: resolvedAccountingDocument.paymentInfo.constantSymbol,
      specificSymbol: resolvedAccountingDocument.paymentInfo.specificSymbol,
    };

    const today = startOfToday();
    const todayDate = getApiDateString(today);
    const dueDate = getApiDateString(
      addDays(today, resolvedAccountingDocument.dueSinceIssueDateInDays ?? CORRECTIVE_TAX_DUE_DAYS)
    );

    const additionalCustomer = resolvedAccountingDocument.additionalCustomer;
    // Reference (`useGetCorrectiveTaxForBCDocumentBody`'s `getRequestBody`) only guards on
    // `customer.id`. This guards on `contractInformationId`/`type` too, which looks stricter, but
    // isn't a real behavior divergence: per `additionalCustomerResponseBodySchema`
    // (`libs/dms/api/finance/src/accounting/types.ts`), only `customerId` is nullable —
    // `contractInformationId`/`type` are non-nullable API fields that are always present whenever
    // `additionalCustomer` itself is present, regardless of `customer.id`. The extra checks exist
    // purely because `useGetDocumentData`'s own `additionalCustomer` mapping builds them via
    // optional chaining (`dataByType.additionalCustomer?.type`), which widens their TS type to
    // `| undefined` even though they're always defined at runtime. Guarding all three fields here
    // (rather than casting) keeps this request body's `additionalCustomer` fully typed with no
    // `as`, with no practical difference from the reference's narrower guard.
    const requestAdditionalCustomer =
      isNotNil(additionalCustomer) &&
      isNotNil(additionalCustomer.customer.id) &&
      isNotNil(additionalCustomer.contractInformationId) &&
      isNotNil(additionalCustomer.type) &&
      isFeatureEnabled(featureFlags.ACCOUNTING_ADDITIONAL_CUSTOMER)
        ? {
            contractInformationId: additionalCustomer.contractInformationId,
            customerId: additionalCustomer.customer.id,
            type: additionalCustomer.type,
          }
        : null;

    const exchangeRateRatioSource = resolvedAccountingDocument.exchangeRateRatio;
    const exchangeRateRatioAmount = resolvedAccountingDocument.isExchangeRateRatioEnabled
      ? buildExchangeRateRatioAmount(exchangeRateRatioSource.amount)
      : null;
    const exchangeRateRatio =
      isNotNil(exchangeRateRatioAmount) && isNotNil(exchangeRateRatioSource.ratio)
        ? {
            amount: exchangeRateRatioAmount,
            currency: exchangeRateRatioSource.currency,
            ratio: exchangeRateRatioSource.ratio,
          }
        : null;

    await calculateTaxDocument({
      vatCheckTotalsDocumentType: 'invoices',
      dueDate: todayDate,
      country: resolvedTenant.country,
      exchangeRateRatio: null,
      branchId: resolvedBranchId,
      paymentType: toRecalculationPaymentType(paymentMethod),
      currency: resolvedTenant.currency,
      items: recalculationItems,
    })
      .unwrap()
      .then(async (summary) => {
        if (isNil(documentSeriesId)) {
          // TYPE CHECK: there should be always present at least system default series.
          throw new Error('Document series not found');
        }

        const requestBody: CorrectiveTaxDocumentRequestBody = {
          dateOfReception: resolvedAccountingDocument.dateOfReception ?? todayDate,
          behindItemsNote: resolvedAccountingDocument.footerNote ?? null,
          customFields: resolvedAccountingDocument.customFields ?? [],
          dateOfTaxableSupply: todayDate,
          description: i18n.t('entity.correctiveTaxDocument.labels.descriptionContent', {
            invoiceNumber: resolvedAccountingDocument.number,
            issueDate: formatDateTime('dateShort', parseISO(todayDate)),
          }),
          documentSeriesId,
          dueDate,
          internalNote: resolvedAccountingDocument.internalNote ?? null,
          issueDate: todayDate,
          paymentInfo,
          additionalCustomer: requestAdditionalCustomer,
          // Explicit product decision for this direct-checkout flow: submit an empty reason
          // rather than routing through the manual form's Zod validation (which would require a
          // non-empty value). `CorrectiveTaxDocumentRequestBodySchema.reason` is a plain
          // `z.string()` with no `.min(1)`, so the backend accepts this.
          reason: '',
          invoicingDocumentType: getInvoicingDocumentType(invoiceType),
          invoicingDocumentId: resolvedInvoicingDocumentId,
          items: buildCorrectiveTaxDocumentItems(mappedItems, resolvedTenant.currency, summary),
          exchangeRateRatio,
        };

        await submitCorrectiveTaxDocumentToBackend({
          businessCaseId,
          orderId,
          paymentId,
          type,
          requestBody,
          onSuccess: () => showNotification.success(i18n.t('general.notifications.success')),
        });
      })
      .catch(handleApiError)
      .finally(() => setIsLoading(false));
  };

  // `false` only while this flow is actually usable (`enabled`) but not yet ready to submit — the
  // consumer (`useGetPaymentDetailAttributes.tsx`) uses this to disable the dropdown trigger
  // itself, rather than leaving the click to silently no-op (see the guard above).
  const isReady =
    !enabled ||
    (isNotNil(tenant) &&
      isNotNil(activeBranchId) &&
      isNotNil(accountingDocument) &&
      isNotNil(order) &&
      isNotNil(businessCase) &&
      isNotNil(documentSeries) &&
      isBranchSynced &&
      (!isBalanceInvoiceFromCheckout || (!isLoadingBalanceItems && !isBalanceItemsError)));

  return [createCorrectiveTaxDocument, {isLoading, isReady}] as const;
};
